/**
 * WorldScene — side-scrolling exploration scene.
 *
 * Optional exploration room type. Not wired to the map yet.
 */

declare const Phaser: any;

import { BaseScene } from "../framework/BaseScene";
import { Entity } from "../framework/Entity";
import { Health } from "./components/Health";
import { ExploreState } from "./states/ExploreState";
import { RunState } from "./data/RunState";

const W = 800;
const H = 450;
const WORLD_WIDTH = 3200;
const GROUND_Y = H - 64;

export class WorldScene extends BaseScene {
  public player!: Entity;
  private entities: Entity[] = [];

  constructor() {
    super({ key: "WorldScene" });
  }

  onReady(_data?: any): void {
    const run: RunState = this.game.registry.get("runState");

    // --- Physics bounds ---
    this.physics.world.setBounds(0, 0, WORLD_WIDTH, H);

    // --- Background layers (parallax) ---
    // Sky
    this.add.rectangle(WORLD_WIDTH / 2, H / 2, WORLD_WIDTH, H, 0x0f1b2d);

    // Stars
    for (let i = 0; i < 80; i++) {
      const sx = Math.random() * WORLD_WIDTH;
      const sy = Math.random() * (H - 100);
      const size = Math.random() * 2 + 1;
      const star = this.add.circle(sx, sy, size, 0xffffff, 0.3 + Math.random() * 0.5);
      star.setScrollFactor(0.2);
    }

    // Distant mountains
    this.createMountains(0x1a2744, 200, 0.3);
    this.createMountains(0x243555, 160, 0.5);

    // Ground
    const ground = this.add.rectangle(WORLD_WIDTH / 2, GROUND_Y + 32, WORLD_WIDTH, 64, 0x2d4a2d);
    this.physics.add.existing(ground, true);

    // Ground detail line
    this.add.rectangle(WORLD_WIDTH / 2, GROUND_Y, WORLD_WIDTH, 2, 0x3d6a3d);

    // --- Player ---
    const playerSprite = this.createPlayerSprite(100, GROUND_Y - 20);
    this.player = new Entity(playerSprite);
    this.player.add(new Health(run.player.hp, run.player.maxHp));
    this.entities.push(this.player);

    // Player collides with ground
    this.physics.add.collider(playerSprite, ground);

    // --- Camera ---
    this.cameras.main.setBounds(0, 0, WORLD_WIDTH, H);
    this.cameras.main.startFollow(playerSprite, true, 0.08, 0.08);

    // --- HUD ---
    this.ui.text(16, 12, "Lv 1", {
      fontFamily: "system-ui, sans-serif",
      fontSize: "14px",
      color: "#9ca3af",
    }, "hud");

    this.ui.text(16, 30, "HP", {
      fontFamily: "system-ui, sans-serif",
      fontSize: "12px",
      color: "#6b7280",
    }, "hud");

    // HP bar background
    this.ui.rect(58, 37, 100, 10, 0x374151, "hud");
    // HP bar fill
    const hpFill = this.ui.rect(58, 37, 100, 10, 0x10b981, "hud");
    hpFill.setOrigin(0.5);

    // --- Register states ---
    const exploreState = new ExploreState();
    this.stateManager.register(exploreState);
    this.stateManager.switch("explore");

    // --- Scatter some environment ---
    this.createTrees();
    this.createEnemyPlaceholder(600);
    this.createEnemyPlaceholder(1400);
    this.createEnemyPlaceholder(2200);
  }

  onUpdate(_time: number, _delta: number): void {
    for (const entity of this.entities) {
      entity.update(_time, _delta);
    }
  }

  // --- Procedural asset creation ---

  private createPlayerSprite(x: number, y: number): any {
    const gfx = this.make.graphics({ x: 0, y: 0, add: false });

    // Body
    gfx.fillStyle(0x3b82f6);
    gfx.fillRect(8, 10, 16, 18);

    // Head
    gfx.fillStyle(0xfbbf24);
    gfx.fillCircle(16, 7, 7);

    // Legs
    gfx.fillStyle(0x1e3a5f);
    gfx.fillRect(10, 28, 5, 8);
    gfx.fillRect(17, 28, 5, 8);

    gfx.generateTexture("world_player", 32, 36);
    gfx.destroy();

    const sprite = this.physics.add.sprite(x, y, "world_player");
    sprite.setCollideWorldBounds(true);
    sprite.body.setGravityY(400);
    sprite.body.setSize(16, 36);

    return sprite;
  }

  private createMountains(color: number, baseY: number, scrollFactor: number): void {
    const gfx = this.add.graphics();
    gfx.fillStyle(color);
    gfx.setScrollFactor(scrollFactor);

    for (let x = 0; x < WORLD_WIDTH; x += 200) {
      const peakH = 40 + Math.random() * 80;
      const w = 150 + Math.random() * 100;
      gfx.fillTriangle(
        x, GROUND_Y,
        x + w / 2, GROUND_Y - peakH,
        x + w, GROUND_Y
      );
    }
  }

  private createTrees(): void {
    for (let i = 0; i < 15; i++) {
      const tx = 200 + Math.random() * (WORLD_WIDTH - 400);
      const ty = GROUND_Y;

      // Trunk
      this.add.rectangle(tx, ty - 20, 6, 40, 0x5c3a1e);

      // Canopy
      const canopyColor = 0x1a5c1a + Math.floor(Math.random() * 0x103010);
      this.add.circle(tx, ty - 45, 18, canopyColor);
    }
  }

  private createEnemyPlaceholder(x: number): void {
    const gfx = this.make.graphics({ x: 0, y: 0, add: false });
    gfx.fillStyle(0xef4444);
    gfx.fillTriangle(12, 0, 24, 16, 12, 32);
    gfx.fillTriangle(12, 0, 0, 16, 12, 32);

    // Eyes
    gfx.fillStyle(0xffffff);
    gfx.fillCircle(8, 13, 3);
    gfx.fillCircle(16, 13, 3);
    gfx.fillStyle(0x000000);
    gfx.fillCircle(8, 13, 1.5);
    gfx.fillCircle(16, 13, 1.5);

    const key = "enemy_" + x;
    gfx.generateTexture(key, 24, 32);
    gfx.destroy();

    const enemy = this.physics.add.sprite(x, GROUND_Y - 16, key);
    enemy.body.setAllowGravity(false);
    enemy.body.setImmovable(true);

    // Float animation
    this.tweens.add({
      targets: enemy,
      y: enemy.y - 8,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });
  }
}
